import { useEffect, useState, type FormEvent } from "react";
import { onAuthError, post, settleReauth } from "../api/client";
import { Button } from "../components/ui/Button";
import { Dialog } from "../components/ui/Dialog";
import { PasswordField, TextField } from "../components/ui/Field";
import { InlineError } from "../components/ui/misc";
import { useSession } from "./session";

/** Prompts for the password (and MFA code) when the API requires recent re-authentication, then retries. */
export function ReauthDialog() {
  const [open, setOpen] = useState(false);
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  const { data: session } = useSession();

  useEffect(() => onAuthError((err) => { if (err.code === "REAUTH_REQUIRED") setOpen(true); }), []);

  const close = (ok: boolean) => {
    setOpen(false);
    setPassword("");
    setCode("");
    setError(null);
    settleReauth(ok);
  };

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await post("/auth/reauthenticate", { password, ...(session?.user.mfaEnabled ? { code } : {}) });
      close(true);
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && close(false)} title="Confirm it's you" description="This action is sensitive. Re-enter your password to continue." size="sm">
      <form onSubmit={submit} className="space-y-3">
        <PasswordField label="Password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} required autoFocus />
        {session?.user.mfaEnabled && (
          <TextField label="Authentication code" inputMode="numeric" autoComplete="one-time-code" value={code} onChange={(e) => setCode(e.target.value)} required />
        )}
        <InlineError error={error} />
        <div className="flex justify-end gap-2">
          <Button onClick={() => close(false)}>Cancel</Button>
          <Button type="submit" variant="primary" loading={busy}>Confirm</Button>
        </div>
      </form>
    </Dialog>
  );
}
